import { toastError } from "../../shared/components/ToastContainer";
import { log, logE } from "../../shared/services/debug-logger";
import { errorLogger } from "../../shared/services/error-logger";
import { api, apiUrl } from "../../shared/services/service";
import { TechForm, TechFormVessel } from "../models/tech-form.model";
import { OTHER_ANTIFOULING_MANUFACTURER_VALUE } from "../models/tq4-antifouling.constants";
import { OptionType, TechFormPaintOption, TechFormPaintOptionsByVendor } from "../models/types";
import { invalidateTechFormSectionDataCacheForAllModes } from "./tech-form-section-data.service";
import { NavigateFunction } from "react-router";

type TechFormPaintOptionsResponse = {
  data: TechFormPaintOption[];
};

type TechFormPortOptionsResponse = {
  data: string[];
};

const toOption = (value: string): OptionType => ({ value, label: value });

const addOtherPaintOption = (options: OptionType[]): OptionType[] => {
  if (options.some((option) => option.value === "Other")) {
    return options;
  }

  return [...options, { value: "Other", label: "Other" }];
};

export async function getTechFormPaintOptions(): Promise<{
  manufacturerOptions: OptionType[];
  paintOptionsByVendor: TechFormPaintOptionsByVendor;
}> {
  const result = await api.get<TechFormPaintOptionsResponse>(`${apiUrl}/tech-form-paint-options`);
  const rows = result.data.data ?? [];
  const vendorLabels = Array.from(new Set(rows.map((row) => row.vendor).filter(Boolean))).sort((a, b) =>
    a.localeCompare(b),
  );
  const paintOptionsByVendor = rows.reduce<TechFormPaintOptionsByVendor>((options, row) => {
    const vendor = row.vendor.trim();
    const paint = row.paint.trim();
    if (!vendor || !paint) {
      return options;
    }

    options[vendor] = options[vendor] ?? [];
    if (!options[vendor].some((option) => option.value === paint)) {
      options[vendor].push(toOption(paint));
    }

    return options;
  }, {});

  Object.keys(paintOptionsByVendor).forEach((vendor) => {
    paintOptionsByVendor[vendor] = addOtherPaintOption(
      paintOptionsByVendor[vendor].sort((a, b) => a.label.localeCompare(b.label)),
    );
  });

  return {
    manufacturerOptions: [
      ...vendorLabels.map(toOption),
      { value: OTHER_ANTIFOULING_MANUFACTURER_VALUE, label: OTHER_ANTIFOULING_MANUFACTURER_VALUE },
    ],
    paintOptionsByVendor,
  };
}

export async function getTechFormPortOptions(): Promise<OptionType[]> {
  const result = await api.get<TechFormPortOptionsResponse>(`${apiUrl}/tech-form-port-options`);

  return (result.data.data ?? []).map(toOption);
}

export async function getPendingTechForms() {
  const result = await api.get(`${apiUrl}/extUserAssignedTechForm`, {
    params: {
      page: 1,
    },
  });

  var forms = result.data.data?.sort(
    (a: TechForm, b: TechForm) => new Date(b.REQUESTED_ON).getTime() - new Date(a.REQUESTED_ON).getTime(),
  );

  forms?.forEach((form: TechForm) => (form.TYPE = "TECH"));

  log("REPO - getPendingTechForms - result", { count: forms?.length ?? 0 });
  return forms;
}

export async function getCompletedTechForms() {
  const result = await api.get(`${apiUrl}/extUserAssignedTechFormStatus`, {
    params: {
      page: 1,
    },
  });

  var forms = result.data.data;

  forms?.forEach((form: TechForm) => (form.TYPE = "TECH"));

  log("REPO - getCompletedTechForms - result", { count: forms?.length ?? 0 });
  return forms;
}

// TQ, BQ
export function openPendingForm(vessel: TechFormVessel, navigate: NavigateFunction) {
  log("SERV - openPendingForm", { type: vessel.TYPE });
  const { IMO_NUMBER: imoNo, VESSEL_NAME: vesselName } = vessel;

  if (vessel.TYPE === "TECH") {
    navigate(`/technicalForm/${imoNo}/${vesselName}/vessel-detail`);
  } else if (vessel.TYPE === "BALTIC") {
    navigate(`/balticForm/${imoNo}/${vesselName}/general-info`);
  }
}

export const saveTq1GenInfo = async (imoNumber: string | undefined, vesselName: string | undefined, _form: any) => {
  log("SERV - saveTq1GenInfo", { imoNumber });
  const section = "Technical Form Page 1 General Info";
  errorLogger.clearErrorBySection(section);
  try {
    await api.post(`${apiUrl}/tq_GeneralInfo`, _form, {
      params: { imoNumber, vesselName },
    });
    log("SERV - saveTq1GenInfo - saved");
    if (imoNumber && vesselName) {
      invalidateTechFormSectionDataCacheForAllModes({ imoNumber, vesselName, section: 1 });
    }
  } catch (error: any) {
    let message = "Error saving Technical Form Page 1 General Info";
    if (error.response?.status === 404) {
      message = "Error saving Technical Form Page 1 General Info - EP not found (404)";
      errorLogger.log(section, message, "TQ");
      log("SERV - saveTq1GenInfo - unavailable", { status: 404 });
      toastError(message);
    } else {
      errorLogger.log(section, message, "TQ");
      logE(new Error("SERV - saveTq1GenInfo - failed"));
      toastError(message);
    }
    throw error;
  }
};

export const saveTq2VesselDesign = async (
  imoNumber: string | undefined,
  vesselName: string | undefined,
  _form: any,
) => {
  log("SERV - saveTq2VesselDesign", { imoNumber });
  const section = "Technical Form Page 2 Vessel Design";
  errorLogger.clearErrorBySection(section);
  try {
    await api.post(`${apiUrl}/tq_Vessel_Design`, _form, {
      params: { imoNumber, vesselName },
    });
    log("SERV - saveTq2VesselDesign - saved");
    if (imoNumber && vesselName) {
      invalidateTechFormSectionDataCacheForAllModes({ imoNumber, vesselName, section: 2 });
    }
  } catch (error: any) {
    let message = "Error saving Technical Form Page 2 Vessel Design";
    if (error.response?.status === 404) {
      message = "Error saving Technical Form Page 2 Vessel Design - EP not found (404)";
      errorLogger.log(section, message, "TQ");
      log("SERV - saveTq2VesselDesign - unavailable", { status: 404 });
      toastError(message);
    } else {
      errorLogger.log(section, message, "TQ");
      logE(new Error("SERV - saveTq2VesselDesign - failed"));
      toastError(message);
    }
    throw error;
  }
};

export const saveTq3VesselDraft = async (imoNumber: string | undefined, vesselName: string | undefined, _form: any) => {
  log("SERV - saveTq3VesselDraft", { imoNumber });
  const section = "Technical Form Page 3 Vessel Draft";
  errorLogger.clearErrorBySection(section);
  try {
    await api.post(`${apiUrl}/tq_Vessel_Draft`, _form, {
      params: { imoNumber, vesselName },
    });
    log("SERV - saveTq3VesselDraft - saved");
    if (imoNumber && vesselName) {
      invalidateTechFormSectionDataCacheForAllModes({ imoNumber, vesselName, section: 3 });
    }
  } catch (error: any) {
    let message = "Error saving Technical Form Page 3 Vessel Draft";
    if (error.response?.status === 404) {
      message = "Error saving Technical Form Page 3 Vessel Draft - EP not found (404)";
      errorLogger.log(section, message, "TQ");
      log("SERV - saveTq3VesselDraft - unavailable", { status: 404 });
      toastError(message);
    } else {
      errorLogger.log(section, message, "TQ");
      logE(new Error("SERV - saveTq3VesselDraft - failed"));
      toastError(message);
    }
    throw error;
  }
};

export const saveTq4AntifoulingDetails = async (
  imoNumber: string | undefined,
  vesselName: string | undefined,
  _form: any,
) => {
  log("SERV - saveTq4AntifoulingDetails", { imoNumber });
  const section = "Technical Form Page 4 Antifouling Details";
  errorLogger.clearErrorBySection(section);
  try {
    await api.post(`${apiUrl}/tq_Antifouling_Details`, _form, {
      params: { imoNumber, vesselName },
    });
    log("SERV - saveTq4AntifoulingDetails - saved");
    if (imoNumber && vesselName) {
      invalidateTechFormSectionDataCacheForAllModes({ imoNumber, vesselName, section: 4 });
    }
  } catch (error: any) {
    let message = "Error saving Technical Form Page 4 Antifouling Details";
    if (error.response?.status === 404) {
      message = "Error saving Technical Form Page 4 Antifouling Details - EP not found (404)";
      errorLogger.log(section, message, "TQ");
      log("SERV - saveTq4AntifoulingDetails - unavailable", { status: 404 });
      toastError(message);
    } else {
      errorLogger.log(section, message, "TQ");
      logE(new Error("SERV - saveTq4AntifoulingDetails - failed"));
      toastError(message);
    }
    throw error;
  }
};

export const saveTq5LastPortCalls = async (
  imoNumber: string | undefined,
  vesselName: string | undefined,
  _form: any,
) => {
  log("SERV - saveTq5LastPortCalls", { imoNumber });
  const section = "Technical Form Page 5 Last Port Calls";
  errorLogger.clearErrorBySection(section);
  try {
    await api.post(`${apiUrl}/tq_Last_Port_Calls`, _form, {
      params: { imoNumber, vesselName },
    });
    log("SERV - saveTq5LastPortCalls - saved");
    if (imoNumber && vesselName) {
      invalidateTechFormSectionDataCacheForAllModes({ imoNumber, vesselName, section: 5 });
    }
  } catch (error: any) {
    let message = "Error saving Technical Form Page 5 Last Port Calls";
    if (error.response?.status === 404) {
      message = "Error saving Technical Form Page 5 Last Port Calls - EP not found (404)";
      errorLogger.log(section, message, "TQ");
      log("SERV - saveTq5LastPortCalls - unavailable", { status: 404 });
      toastError(message);
    } else {
      errorLogger.log(section, message, "TQ");
      logE(new Error("SERV - saveTq5LastPortCalls - failed"));
      toastError(message);
    }
    throw error;
  }
};

export const saveTq6Seca = async (imoNumber: string | undefined, vesselName: string | undefined, _form: any) => {
  log("SERV - saveTq6Seca", { imoNumber });
  const section = "Technical Form Page 6 SECA";
  errorLogger.clearErrorBySection(section);
  try {
    await api.post(`${apiUrl}/tq_Seca`, _form, {
      params: { imoNumber, vesselName },
    });
    log("SERV - saveTq6Seca - saved");
    if (imoNumber && vesselName) {
      invalidateTechFormSectionDataCacheForAllModes({ imoNumber, vesselName, section: 6 });
    }
  } catch (error: any) {
    let message = "Error saving Technical Form Page 6 SECA";
    if (error.response?.status === 404) {
      message = "Error saving Technical Form Page 6 SECA - EP not found (404)";
      errorLogger.log(section, message, "TQ");
      log("SERV - saveTq6Seca - unavailable", { status: 404 });
      toastError(message);
    } else {
      errorLogger.log(section, message, "TQ");
      logE(new Error("SERV - saveTq6Seca - failed"));
      toastError(message);
    }
    throw error;
  }
};

export const saveTq7SlowSteaming = async (
  imoNumber: string | undefined,
  vesselName: string | undefined,
  _form: any,
) => {
  log("SERV - saveTq7SlowSteaming", { imoNumber });
  const section = "Technical Form Page 7 Slow Steaming";
  errorLogger.clearErrorBySection(section);
  try {
    await api.post(`${apiUrl}/tq_Slow_Steaming`, _form, {
      params: { imoNumber, vesselName },
    });
    log("SERV - saveTq7SlowSteaming - saved");
    if (imoNumber && vesselName) {
      invalidateTechFormSectionDataCacheForAllModes({ imoNumber, vesselName, section: 7 });
    }
  } catch (error: any) {
    let message = "Error saving Technical Form Page 7 Slow Steaming";
    if (error.response?.status === 404) {
      message = "Error saving Technical Form Page 7 Slow Steaming - EP not found (404)";
      errorLogger.log(section, message, "TQ");
      log("SERV - saveTq7SlowSteaming - unavailable", { status: 404 });
      toastError(message);
    } else {
      errorLogger.log(section, message, "TQ");
      logE(new Error("SERV - saveTq7SlowSteaming - failed"));
      toastError(message);
    }
    throw error;
  }
};

export const saveTq8PowerManagement = async (
  imoNumber: string | undefined,
  vesselName: string | undefined,
  _form: any,
) => {
  log("SERV - saveTq8PowerManagement", { imoNumber });
  const section = "Technical Form Page 8 Power Management";
  errorLogger.clearErrorBySection(section);
  try {
    await api.post(`${apiUrl}/tq_Power_Management`, _form, {
      params: { imoNumber, vesselName },
    });
    log("SERV - saveTq8PowerManagement - saved");
    if (imoNumber && vesselName) {
      invalidateTechFormSectionDataCacheForAllModes({ imoNumber, vesselName, section: 8 });
    }
  } catch (error: any) {
    let message = "Error saving Technical Form Page 8 Power Management";
    if (error.response?.status === 404) {
      message = "Error saving Technical Form Page 8 Power Management - EP not found (404)";
      errorLogger.log(section, message, "TQ");
      log("SERV - saveTq8PowerManagement - unavailable", { status: 404 });
      toastError(message);
    } else {
      errorLogger.log(section, message, "TQ");
      logE(new Error("SERV - saveTq8PowerManagement - failed"));
      toastError(message);
    }
    throw error;
  }
};
